import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase.js';
import { renderTopbar } from '../components/topbar.js';
import { currentUser, isSchoolOwnerCached, isAdminCached } from '../lib/auth.js';
import { getTopic, getTopics, fetchVersionHtml } from '../lib/topics.js';
import { getCourses } from '../lib/courses.js';
import {
  watchActiveListings, watchMyListings, watchMyPurchases,
  purchaseListing, quotePurchase, getPurchaseContent, getListingPreview, delistListing, relistListing, approveVersion,
  voteListing, getMyVote, filePurchase, reportPurchase, isExpired, ACCESS_DURATION_LABELS,
  deleteListingForever, searchMarketplaceListingsSemantic,
  watchMarketplaceLabels,
} from '../lib/marketplace.js';
import { formatPaise } from '../lib/wallet.js';
import { navigate } from '../router.js';
import { injectAskAIBridge, attachAskAIBridge, injectCanvasEnhancements } from '../lib/askAI.js';
import { loadReadOnlyFrame } from '../lib/readOnlyFrame.js';
import { attachPurchaseQuizScoreBridge } from '../lib/quizScore.js';
import { inspectGeneratedHtml, isWellFormedHtml, testRenderForErrors } from '../lib/sandbox.js';
import { checkViewingAccessUnlessFree, renderViewingGate } from '../lib/viewingAccess.js';
import { friendlyError } from '../lib/friendlyError.js';
import { alertDialog, confirmDialog } from '../lib/dialog.js';
import { firstContentItem, hasTreeStructure, renderContentTree, wireContentTree } from '../lib/contentTree.js';

// Optional, seller-triggered content check — NOT run automatically as part
// of approving a version. It used to gate "Approve current version" (throwing
// on the first problem found), but the underlying heuristics (inspectGeneratedHtml/
// testRenderForErrors — best-effort structural/visual checks, not a hard
// correctness guarantee) have false positives, and a false positive there
// meant a seller could never approve their own already-fine content. Sellers
// can still run this any time via "Check content quality" to see what the
// automated checks flag, but it's advisory only — it never blocks approval.
async function checkListingContent(uid, listing) {
  const topics = listing.type === 'topic'
    ? [await getTopic(uid, listing.courseId, listing.topicId)]
    : await getTopics(uid, listing.courseId);
  const generated = topics.filter((topic) => topic?.currentVersion);
  if (!generated.length) return ['There is no generated content to check yet.'];
  const problems = [];
  for (const topic of generated) {
    const html = await fetchVersionHtml(uid, listing.courseId, topic.id, topic.currentVersion);
    const topicProblems = [];
    if (!isWellFormedHtml(html)) topicProblems.push('HTML document is incomplete.');
    topicProblems.push(...inspectGeneratedHtml(html));
    topicProblems.push(...await testRenderForErrors(html));
    if (topicProblems.length) {
      const extra = topicProblems.length > 1 ? ` (+${topicProblems.length - 1} more issue${topicProblems.length > 2 ? 's' : ''})` : '';
      problems.push(`${topic.title || 'Topic'}: ${topicProblems[0]}${extra}`);
    }
  }
  return problems;
}

// Above this many items in a multi-topic listing, switch the item picker
// from one-button-per-topic tabs to a dropdown — a big course (e.g.
// Physics) otherwise renders a wall of topic-name buttons that covers
// the screen. Kept in sync with the same constant in views/course.js,
// which renders the same purchased content inline in a course's tree.
const ITEM_TABS_TO_DROPDOWN_THRESHOLD = 6;

const TABS = [
  { id: 'browse', label: 'Browse' },
  { id: 'purchases', label: 'My Library' },
  { id: 'selling', label: 'My listings' },
];

export function renderMarketplace(container) {
  container.innerHTML = '';
  container.appendChild(renderTopbar('/marketplace'));

  const uid = currentUser().uid;
  // Deep-link support (e.g. the "🟠 Update available" badge on a home-page
  // course card, src/views/home.js) — the router strips everything from
  // '?' onward before matching the route (router.js) but doesn't forward
  // it, so it's read directly off the hash here instead.
  const requestedTab = new URLSearchParams(location.hash.split('?')[1] || '').get('tab');
  let activeTab = TABS.some((t) => t.id === requestedTab) ? requestedTab : 'browse';
  const unsubs = [];

  const main = document.createElement('div');
  main.className = 'main-full';
  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:8px;">
      <div>
        <h1 style="margin-bottom:4px;">Library</h1>
        <p style="color:var(--text-dim);margin-top:0;">Courses and topics curated by Learning Lab.</p>
      </div>
    </div>
    <div style="display:flex;gap:6px;border-bottom:1px solid var(--border);margin-bottom:20px;" id="mp-tabs">
      ${TABS.map((t) => `<button class="btn btn-ghost mp-tab" data-tab="${t.id}" style="border-radius:0;border-bottom:2px solid transparent;">${t.label}</button>`).join('')}
    </div>
    <div id="mp-body"></div>
  `;
  container.appendChild(main);

  const body = main.querySelector('#mp-body');
  const tabButtons = main.querySelectorAll('.mp-tab');

  function highlightTabs() {
    tabButtons.forEach((b) => {
      const on = b.dataset.tab === activeTab;
      b.style.borderBottomColor = on ? 'var(--accent)' : 'transparent';
      b.style.color = on ? 'var(--text)' : 'var(--text-dim)';
      b.style.fontWeight = on ? '700' : '600';
    });
  }

  function clearBodySubscriptions() {
    while (unsubs.length) unsubs.pop()();
  }

  function renderActiveTab() {
    clearBodySubscriptions();
    highlightTabs();
    if (activeTab === 'browse') renderBrowseTab(body, uid, unsubs);
    else if (activeTab === 'purchases') renderPurchasesTab(body, uid, unsubs);
    else renderSellingTab(body, uid, unsubs);
  }

  tabButtons.forEach((b) => b.addEventListener('click', () => { activeTab = b.dataset.tab; renderActiveTab(); }));
  renderActiveTab();

  return () => clearBodySubscriptions();
}

// Whether a listing has ANY content a buyer could actually receive right
// now. A listing can exist (and even be status:'active') before the seller
// has ever clicked "Approve current version" — see the APPROVAL_NOTICE in
// renderSellingTab — in which case purchaseListing/getPurchaseContent would
// just reject with "The seller has not approved any content for
// distribution yet." Rather than let a buyer discover that at checkout,
// Browse filters these out entirely: nothing to preview or buy yet, so
// nothing to show. It reappears on its own the moment the seller approves,
// since this reads straight off the same listing doc Browse already has
// (no extra fetch needed).
function hasApprovedContent(listing) {
  return listing.type === 'topic'
    ? !!listing.approvedVersion
    : !!(listing.approvedVersions && Object.keys(listing.approvedVersions).length);
}

// Listing prices are set by sellers in rupees.
function formatPrice(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ── Browse ────────────────────────────────────────────────────────────
// Shared by both the instant substring grid and the semantic results
// section below — same card markup, same button wiring, so the two feel
// like one continuous result list rather than two different UIs bolted
// together.
function listingCardHtml(l, owned, badgeLabel) {
  const isFree = Number(l.viewPrice || 0) <= 0;
  return `
    <div class="card${isFree ? ' free-listing-card' : ''}" data-id="${l.id}" style="cursor:default;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;">
        <div class="card-emoji">${l.emoji || '📘'}</div>
        ${badgeLabel ? `<span class="pill" title="Recommended by your school">⭐ ${escapeHtml(badgeLabel)}</span>` : ''}
      </div>
      <h3>${escapeHtml(l.title)}</h3>
      ${l.type === 'topic' && l.courseTitle ? `<p style="margin:0 0 4px;color:var(--text-dim);font-size:0.82rem;">Part of ${escapeHtml(l.courseTitle)}</p>` : ''}
      <p>${escapeHtml(l.description || 'No description')}</p>
      <p class="pill" style="margin-top:6px;">${l.type === 'course' ? 'Whole course' : 'Single topic'} · ${ACCESS_DURATION_LABELS[l.accessDuration] || '1 year'}</p>
      <p style="margin:6px 0 0;color:var(--text-dim);font-size:0.82rem;">👍 ${l.upvotes || 0} 👎 ${l.downvotes || 0}</p>
      <div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
        <button class="btn preview-btn" data-id="${l.id}">👀 Preview (no controls)</button>
        ${owned
          ? `<p class="pill" style="width:fit-content;">✅ You already have access</p>
             <button class="btn btn-primary open-owned-btn" data-id="${l.id}" data-purchase-id="${owned.id}">Open</button>`
          : `<button class="btn btn-primary buy-btn" data-id="${l.id}">${Number(l.viewPrice || 0) <= 0 ? 'Get access — Free' : `Get access — ${formatPrice(l.viewPrice)}`}</button>`}
      </div>
    </div>
  `;
}

function wireListingCardButtons(container, listings, uid, activePurchaseByListing) {
  container.querySelectorAll('.buy-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const listing = listings.find((l) => l.id === btn.dataset.id);
      openCheckoutModal(listing);
    });
  });
  container.querySelectorAll('.preview-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const listing = listings.find((l) => l.id === btn.dataset.id);
      openPreviewViewer(listing);
    });
  });
  container.querySelectorAll('.open-owned-btn').forEach((btn) => {
    btn.addEventListener('click', () => openPurchaseViewer(uid, btn.dataset.purchaseId, btn.dataset.id));
  });
}

function renderBrowseTab(body, uid, unsubs) {
  body.innerHTML = `
    <div id="mp-preferred-sections"></div>
    <div class="field" style="max-width:420px;margin-bottom:8px;">
      <input type="text" id="mp-search" placeholder="🔎 Search by title, course, or description…" autocomplete="off" />
    </div>
    <label style="display:flex;align-items:center;gap:6px;font-size:0.85rem;color:var(--text-dim);margin-bottom:16px;cursor:pointer;width:fit-content;">
      <input type="checkbox" id="mp-hide-owned" checked style="width:auto;">
      Hide items you already have
    </label>
    <div class="grid-cards" id="mp-grid"></div>
    <div id="mp-semantic-section" style="display:none;margin-top:22px;">
      <h3 style="font-size:0.9rem;color:var(--text-dim);margin-bottom:10px;">More results (related by meaning)</h3>
      <div class="grid-cards" id="mp-semantic-grid"></div>
    </div>
  `;
  const grid = body.querySelector('#mp-grid');
  const searchInput = body.querySelector('#mp-search');
  const hideOwnedToggle = body.querySelector('#mp-hide-owned');
  const semanticSection = body.querySelector('#mp-semantic-section');
  const semanticGrid = body.querySelector('#mp-semantic-grid');
  const preferredContainer = body.querySelector('#mp-preferred-sections');
  let allOthers = [];
  let searchTerm = '';
  let currentOthers = [];
  // Enabled by default — Browse is meant for finding something NEW; a
  // buyer's already-owned items clutter it (they can always still find one
  // via "My purchases", or flip this off to look at everything again).
  let hideOwned = true;
  // listingId -> the buyer's own still-valid (unexpired) purchase of it, if
  // any. Used to decide whether Browse offers "Get access" or "Open" (and,
  // when hideOwned is on, to filter owned listings out entirely) — the
  // actual no-double-purchase-while-active rule is re-enforced server-side
  // in purchaseListing regardless of what this shows.
  let activePurchaseByListing = {};

  // Admin-curated "Preferred list" sections (see functions/marketplaceLabels.js
  // and the admin panel's Curated Labels editor) — one section per non-empty
  // label, above the ordinary grid, school-affiliated accounts only (owner,
  // teacher, or a linked student). isSchoolLinked resolves once below;
  // labels/allOthers each arrive independently (Firestore listener vs. the
  // outer watchActiveListings), so both paths re-render whichever is ready.
  let labels = [];
  let isSchoolLinked = isSchoolOwnerCached();
  // ONE combined grid across every label (not a full-width block per
  // label) so cards pack horizontally the same way the ordinary Browse
  // grid does, regardless of how many labels exist or how few listings any
  // one of them has — each card keeps its own ⭐ label-name badge instead
  // of a separate section heading, so a listing that's only in one small
  // label doesn't end up stranded alone on its own row. A listing tagged by
  // more than one label appears once, badged with the first label it
  // matches.
  function renderPreferredSections() {
    if (!isSchoolLinked) { preferredContainer.innerHTML = ''; return; }
    const byId = new Map(allOthers.map((l) => [l.id, l]));
    const seen = new Set();
    const entries = [];
    labels.forEach((label) => {
      (label.listingIds || []).forEach((id) => {
        if (seen.has(id)) return;
        const listing = byId.get(id);
        if (!listing) return;
        if (hideOwned && activePurchaseByListing[id]) return;
        seen.add(id);
        entries.push({ listing, labelName: label.name });
      });
    });
    if (entries.length === 0) { preferredContainer.innerHTML = ''; return; }
    preferredContainer.innerHTML = `
      <h3 style="font-size:0.95rem;margin-bottom:10px;">⭐ Recommended for your school</h3>
      <div class="grid-cards" id="mp-preferred-grid">
        ${entries.map((e) => listingCardHtml(e.listing, activePurchaseByListing[e.listing.id], e.labelName)).join('')}
      </div>
    `;
    const preferredGrid = preferredContainer.querySelector('#mp-preferred-grid');
    wireListingCardButtons(preferredGrid, entries.map((e) => e.listing), uid, activePurchaseByListing);
  }
  // profile.schoolCode covers a linked student/teacher; isSchoolOwnerCached
  // (checked eagerly above) covers the owner, who has no schoolCode on
  // their own profile — see functions/wallet.js resolveEntitlementContext.
  if (!isSchoolLinked) {
    getDoc(doc(db, 'users', uid, 'profile', 'main')).then((snap) => {
      if (snap.exists() && snap.data().schoolCode) {
        isSchoolLinked = true;
        renderPreferredSections();
      }
    }).catch(() => {});
  }
  const unsubLabels = watchMarketplaceLabels((rows) => {
    labels = rows;
    renderPreferredSections();
  });
  unsubs.push(unsubLabels);

  function renderGrid() {
    const visible = hideOwned ? allOthers.filter((l) => !activePurchaseByListing[l.id]) : allOthers;
    // Also matches the PARENT course's title, not just the listing's own
    // title/description — for a single-topic listing, `l.title` is only
    // the topic's name, so a search for the course it lives in (e.g.
    // "Geometry Lab") would otherwise miss it entirely. See courseTitle in
    // functions/marketplace.js listForSale.
    const others = searchTerm
      ? visible.filter((l) => `${l.title} ${l.courseTitle || ''} ${l.description || ''}`.toLowerCase().includes(searchTerm))
      : visible;
    currentOthers = others;

    if (allOthers.length === 0) {
      grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1;">
        <div style="font-size:2.5rem;">🛍️</div>
        <h3>Nothing for sale yet</h3>
        <p>Once someone lists a course or topic, it'll show up here.</p>
      </div>`;
      return;
    }
    if (visible.length === 0 && !searchTerm) {
      grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1;">
        <div style="font-size:2.5rem;">✅</div>
        <h3>You already have everything available</h3>
        <p>Turn off "Hide items you already have" above to see it again.</p>
      </div>`;
      return;
    }
    if (others.length === 0) {
      grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1;">
        <div style="font-size:2.5rem;">🔎</div>
        <h3>No matches</h3>
        <p>Nothing matches "${escapeHtml(searchTerm)}" — try a different search, or check below for semantically related results.</p>
      </div>`;
      return;
    }
    grid.innerHTML = others.map((l) => listingCardHtml(l, activePurchaseByListing[l.id])).join('');
    wireListingCardButtons(grid, others, uid, activePurchaseByListing);
  }

  // Semantic upgrade (see functions/search.js) — runs alongside the instant
  // substring filter above, debounced since it's a real Cloud Function
  // round trip rather than an in-memory filter. Appended as its OWN section
  // below the main grid rather than replacing it, and excludes anything the
  // substring filter already surfaced so the two never show duplicate cards.
  let semanticDebounce = null;
  let semanticToken = 0;
  function runSemanticSearch(term) {
    clearTimeout(semanticDebounce);
    if (term.length < 3) {
      semanticSection.style.display = 'none';
      semanticGrid.innerHTML = '';
      return;
    }
    semanticDebounce = setTimeout(async () => {
      const token = ++semanticToken;
      try {
        const results = await searchMarketplaceListingsSemantic(term, { excludeUid: uid });
        if (token !== semanticToken) return;
        const shownIds = new Set(currentOthers.map((l) => l.id));
        const extra = results.filter((l) => !shownIds.has(l.id) && !(hideOwned && activePurchaseByListing[l.id]));
        if (extra.length === 0) { semanticSection.style.display = 'none'; semanticGrid.innerHTML = ''; return; }
        semanticSection.style.display = 'block';
        semanticGrid.innerHTML = extra.map((l) => listingCardHtml(l, activePurchaseByListing[l.id])).join('');
        wireListingCardButtons(semanticGrid, extra, uid, activePurchaseByListing);
      } catch (_) { /* best-effort — the instant substring layer above already stands on its own */ }
    }, 400);
  }

  searchInput.addEventListener('input', () => {
    searchTerm = searchInput.value.trim().toLowerCase();
    renderGrid();
    runSemanticSearch(searchTerm);
  });

  hideOwnedToggle.addEventListener('change', () => {
    hideOwned = hideOwnedToggle.checked;
    renderGrid();
    renderPreferredSections();
    if (searchTerm) runSemanticSearch(searchTerm);
  });

  const unsub = watchActiveListings((listings) => {
    allOthers = listings.filter((l) => l.sellerUid !== uid && hasApprovedContent(l));
    renderGrid();
    renderPreferredSections();
  });
  unsubs.push(unsub);

  const unsubPurchases = watchMyPurchases(uid, (purchases) => {
    activePurchaseByListing = {};
    purchases.forEach((p) => {
      if (!isExpired(p)) activePurchaseByListing[p.listingId] = p;
    });
    renderGrid();
    renderPreferredSections();
  });
  unsubs.push(unsubPurchases);
}

// ── Preview (free teaser, no purchase) ───────────────────────────────────
//
// Injected into the fetched HTML before it's ever set as an iframe's
// srcdoc. Two things happen once the underlying page finishes loading:
//   1. A transparent full-viewport blocker eats click/mousedown/pointerdown/
//      keydown/contextmenu/dragstart — i.e. anything a simulator control,
//      button, drag handle, or keyboard shortcut would use — so the page
//      renders and any of its own animations/timers keep running, but a
//      visitor can't operate any control without buying access. Wheel/touch
//      scroll events are deliberately left alone so the page can still be
//      scrolled to see what's there.
//   2. Once past the halfway point of the page's total height, the content
//      fades under a blurred veil with a "purchase to see the rest" lock,
//      so only the top half is ever actually legible.
// This is best-effort (same honest caveat as view-only purchases — a
// determined visitor can inspect devtools) but stops casual control use.
const PREVIEW_LOCK_SNIPPET = `
<script>
(function () {
  function lockDown() {
    try {
      document.body.style.position = document.body.style.position || 'relative';
      var totalH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, window.innerHeight);
      var halfH = totalH * 0.5;

      var blocker = document.createElement('div');
      blocker.id = '__ll_preview_blocker';
      Object.assign(blocker.style, {
        position: 'fixed', inset: '0', zIndex: '2147483000',
        background: 'transparent', cursor: 'not-allowed',
      });
      ['click','dblclick','mousedown','mouseup','pointerdown','pointerup','contextmenu','dragstart','keydown','keyup']
        .forEach(function (evt) {
          blocker.addEventListener(evt, function (e) { e.preventDefault(); e.stopPropagation(); }, true);
        });
      document.body.appendChild(blocker);

      var fade = document.createElement('div');
      fade.id = '__ll_preview_fade';
      Object.assign(fade.style, {
        position: 'absolute', left: '0', right: '0', top: halfH + 'px',
        height: Math.max(totalH - halfH, 40) + 'px', pointerEvents: 'none', zIndex: '2147482900',
        display: 'flex', justifyContent: 'center',
        background: 'linear-gradient(to bottom, rgba(247,245,240,0) 0px, rgba(247,245,240,0.85) 140px, rgba(247,245,240,0.98) 320px, rgba(247,245,240,0.98) 100%)',
        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      });
      var lock = document.createElement('div');
      lock.textContent = '🔒 Get access to see and interact with the rest';
      Object.assign(lock.style, {
        position: 'sticky', top: '45vh', height: 'fit-content', margin: '0 auto',
        background: '#16181f', color: '#fff', padding: '12px 20px', borderRadius: '999px',
        fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '14px', fontWeight: '700',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', whiteSpace: 'nowrap',
      });
      fade.appendChild(lock);
      document.body.appendChild(fade);
    } catch (e) { /* best-effort preview lock; never break the underlying page */ }
  }
  if (document.readyState === 'complete') lockDown();
  else window.addEventListener('load', lockDown);
})();
</script>`;

function wrapHtmlForPreview(html) {
  const source = injectCanvasEnhancements(html);
  if (/<\/body>/i.test(source)) return source.replace(/<\/body>/i, `${PREVIEW_LOCK_SNIPPET}\n</body>`);
  return `${source}\n${PREVIEW_LOCK_SNIPPET}`;
}

﻿async function openPreviewViewer(listing) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="width:min(90vw, 960px);height:80vh;display:flex;flex-direction:column;">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <h2 style="margin:0;">Loading preview...</h2>
        <button class="btn btn-sm" id="close-viewer-btn">Close</button>
      </div>
      <p class="pill" style="margin:6px 0 0;width:fit-content;">Preview only - controls are disabled and the bottom half is locked.</p>
      <div id="viewer-body" style="flex:1;margin-top:14px;overflow:hidden;display:flex;flex-direction:column;">
        <div class="empty-state">Fetching preview...</div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('#close-viewer-btn').addEventListener('click', () => overlay.remove());

  try {
    const content = await getListingPreview(listing.id);
    overlay.querySelector('h2').textContent = content.title;
    const viewerBody = overlay.querySelector('#viewer-body');
    const contentItems = (content.items || []).filter((it) => it.kind !== 'index' && it.html);
    const selected = firstContentItem(content.items) || contentItems[0];
    const renderAsTree = hasTreeStructure(content.items);
    const useDropdown = contentItems.length > ITEM_TABS_TO_DROPDOWN_THRESHOLD;
    viewerBody.innerHTML = `
      ${renderAsTree ? `
        <div class="readonly-content-viewer">
          ${renderContentTree(content.items, selected?.topicId)}
          <div id="preview-viewer-frame-wrap" style="flex:1;min-height:0;width:100%;overflow:hidden;background:#fff;position:relative;">
            <iframe id="preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
          </div>
        </div>
      ` : `
        ${contentItems.length > 1 ? (useDropdown
          ? `<select class="item-select" style="margin-bottom:10px;max-width:100%;flex-shrink:0;">
              ${contentItems.map((it, i) => `<option value="${i}">${escapeHtml(it.title)}</option>`).join('')}
            </select>`
          : `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;flex-shrink:0;">
              ${contentItems.map((it, i) => `<button class="btn btn-sm item-tab" data-i="${i}">${escapeHtml(it.title)}</button>`).join('')}
            </div>`) : ''}
        <div id="preview-viewer-frame-wrap" style="flex:1;min-height:0;width:100%;overflow:hidden;background:#fff;position:relative;">
          <iframe id="preview-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
        </div>
      `}
    `;

    function showItem(item) {
      const frame = viewerBody.querySelector('#preview-frame');
      const wrap = viewerBody.querySelector('#preview-viewer-frame-wrap');
      if (!item || !frame || !wrap) return;
      const spinner = document.createElement('div');
      spinner.className = 'content-loading-overlay';
      spinner.innerHTML = '<div class="content-loading-spinner"></div>';
      wrap.appendChild(spinner);
      frame.addEventListener('load', () => spinner.remove(), { once: true });
      frame.srcdoc = wrapHtmlForPreview(item.html);
    }
    if (renderAsTree) {
      wireContentTree(viewerBody.querySelector('.readonly-content-tree'), content.items, { initialTopicId: selected?.topicId, onSelect: showItem });
    } else {
      showItem(selected);
    }
    viewerBody.querySelectorAll('.item-tab').forEach((b) => b.addEventListener('click', () => showItem(contentItems[Number(b.dataset.i)])));
    viewerBody.querySelector('.item-select')?.addEventListener('change', (e) => showItem(contentItems[Number(e.target.value)]));
  } catch (err) {
    overlay.querySelector('#viewer-body').innerHTML = `<div class="empty-state">${escapeHtml(friendlyError(err, "Couldn't load a preview."))}</div>`;
  }
}

async function openCheckoutModal(listing) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>Checkout</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">${escapeHtml(listing.title)} - access</p>
      <div id="quote-body"><div class="empty-state">Fetching price...</div></div>
      <div id="pay-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="cancel-btn">Cancel</button>
        <button class="btn btn-primary" id="pay-btn" disabled>Pay</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());

  const quoteBody = overlay.querySelector('#quote-body');
  const payBtn = overlay.querySelector('#pay-btn');
  const errEl = overlay.querySelector('#pay-error');
  let quote;
  try {
    quote = await quotePurchase(listing.id);
  } catch (err) {
    quoteBody.innerHTML = `<div class="empty-state">${escapeHtml(friendlyError(err, "Couldn't fetch the price."))}</div>`;
    return;
  }

  const isFree = (quote.payablePaise || 0) <= 0;
  const enough = quote.walletBalancePaise >= quote.payablePaise;
  quoteBody.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:6px;margin:10px 0 14px;">
      <div style="display:flex;justify-content:space-between;font-size:1.2rem;font-weight:800;">
        <span>You pay</span><span>${isFree ? 'Free' : formatPaise(quote.payablePaise)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;color:${enough ? 'var(--text-dim)' : 'var(--danger)'};font-size:0.88rem;">
        <span>Wallet balance</span><span>${formatPaise(quote.walletBalancePaise)}</span>
      </div>
    </div>
    ${enough ? '' : `<div class="pill" style="display:block;margin-bottom:10px;background:var(--surface-2);">Your wallet balance is too low - recharge it, then come back to finish adding this item.</div>`}
  `;

  if (!enough && !isFree) {
    payBtn.disabled = false;
    payBtn.textContent = 'Recharge wallet';
    payBtn.addEventListener('click', () => { overlay.remove(); navigate('/wallet'); });
    return;
  }

  payBtn.disabled = false;
  payBtn.textContent = isFree ? 'Get for free' : `Pay ${formatPaise(quote.payablePaise)} from wallet`;
  payBtn.addEventListener('click', async () => {
    errEl.textContent = '';
    payBtn.disabled = true;
    payBtn.textContent = isFree ? 'Getting...' : 'Processing...';
    try {
      await purchaseListing(listing.id);
      overlay.remove();
      showToast(isSchoolOwnerCached() ? 'Added - now available to every student in your school.' : 'Added - check My Library.');
    } catch (err) {
      errEl.textContent = friendlyError(err, isFree ? 'Could not add this item.' : 'Payment failed.');
      payBtn.disabled = false;
      payBtn.textContent = isFree ? 'Get for free' : `Pay ${formatPaise(quote.payablePaise)} from wallet`;
    }
  });
}

function showToast(message) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3500);
}

function canReportPurchase(purchase) {
  if (purchase.complaintStatus && purchase.complaintStatus !== 'none') return false;
  const deadline = purchase.complaintDeadlineAt?.toMillis?.() ?? purchase.complaintDeadlineAt;
  return !!deadline && deadline > Date.now();
}

function renderPurchasesTab(body, uid, unsubs) {
  body.innerHTML = `<div id="mp-purchases"></div>`;
  const list = body.querySelector('#mp-purchases');

  function renderList(purchases) {
    list.innerHTML = `<div class="grid-cards">${purchases.map((p) => {
      const expired = isExpired(p);
      const canReport = canReportPurchase(p);
      return `
        <div class="card" style="cursor:default;">
            <div class="card-emoji">${p.listingEmoji || '📘'}</div>
            <h3>${escapeHtml(p.listingTitle || 'Untitled')}</h3>
            <p>${escapeHtml(p.listingDescription || 'No description')}</p>
            <p class="pill">${expired ? 'Expired' : (p.expiresAt ? 'Expires ' + new Date(p.expiresAt.toMillis?.() ?? p.expiresAt).toLocaleDateString() : 'Active')}</p>
          ${p.complaintStatus && p.complaintStatus !== 'none' ? `<p class="pill" style="width:fit-content;">Report status: ${escapeHtml(p.complaintStatus)}</p>` : ''}
          <p style="margin:4px 0 0;color:var(--text-dim);font-size:0.8rem;">${p.filedCourseId ? 'Filed in your tree' : 'Not organized yet'}</p>
          <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap;">
            <button class="btn btn-primary open-purchase-btn" data-id="${p.id}" data-listing-id="${p.listingId}" style="flex:1;justify-content:center;" ${expired ? 'disabled' : ''}>${expired ? 'Access expired' : 'Open'}</button>
            ${canReport ? `<button class="btn btn-sm report-purchase-btn" data-id="${p.id}">Report issue</button>` : ''}
            <button class="btn btn-sm organize-btn" data-id="${p.id}">File</button>
          </div>
        </div>
      `;
    }).join('')}</div>`;

    list.querySelectorAll('.open-purchase-btn').forEach((btn) => btn.addEventListener('click', () => openPurchaseViewer(uid, btn.dataset.id, btn.dataset.listingId)));
    list.querySelectorAll('.organize-btn').forEach((btn) => btn.addEventListener('click', () => openOrganizeModal(uid, purchases.find((p) => p.id === btn.dataset.id))));
    list.querySelectorAll('.report-purchase-btn').forEach((btn) => btn.addEventListener('click', () => openReportPurchaseModal(purchases.find((p) => p.id === btn.dataset.id))));
  }

  const unsub = watchMyPurchases(uid, (purchases) => {
    if (purchases.length === 0) {
      list.innerHTML = `<div class="empty-state"><div style="font-size:2.5rem;">📚</div><h3>Your Library is empty</h3><p>Add something from Browse and it'll show up here.</p></div>`;
      return;
    }
    renderList(purchases);
  });
  unsubs.push(unsub);
}

export async function openPurchaseViewer(uid, purchaseId, listingId) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal modal-fullscreen" style="display:flex;flex-direction:column;">
      <div class="modal-fullscreen-header" style="display:flex;justify-content:space-between;align-items:center;">
        <h2 style="margin:0;">Loading...</h2>
        <button class="btn btn-sm" id="close-viewer-btn">Close</button>
      </div>
      <div id="viewer-body" class="modal-fullscreen-body"><div class="empty-state">Loading...</div></div>
    </div>
  `;
  document.body.appendChild(overlay);
  let detachAskAI = null;
  let detachQuizScore = null;
  overlay.querySelector('#close-viewer-btn').addEventListener('click', () => {
    detachAskAI?.();
    detachQuizScore?.();
    overlay.remove();
  });

  let listingSnap = null;
  try { listingSnap = await getDoc(doc(db, 'marketplace', listingId)); } catch (e) {}
  const isFree = !!(listingSnap && listingSnap.exists()) && Number(listingSnap.data().viewPrice || 0) <= 0;

  async function loadBody() {
    const access = await checkViewingAccessUnlessFree(isFree);
    if (!overlay.isConnected) return;
    if (access.blocked) {
      overlay.querySelector('h2').textContent = access.schoolLapsed ? 'School subscription ended' : 'Renew to keep viewing';
      renderViewingGate(overlay.querySelector('#viewer-body'), access, loadBody);
      return;
    }
    try {
      const [content, myVote] = await Promise.all([getPurchaseContent(purchaseId), getMyVote(uid, listingId)]);
      if (!overlay.isConnected) return;
      overlay.querySelector('h2').textContent = content.title;
      const viewerBody = overlay.querySelector('#viewer-body');
      const contentItems = (content.items || []).filter((it) => it.kind !== 'index' && it.html);
      const selected = firstContentItem(content.items) || contentItems[0];
      let voteState = {
        upvotes: listingSnap?.exists() ? (listingSnap.data().upvotes || 0) : 0,
        downvotes: listingSnap?.exists() ? (listingSnap.data().downvotes || 0) : 0,
        yourVote: myVote,
      };
      const renderAsTree = hasTreeStructure(content.items);
      const useDropdown = contentItems.length > ITEM_TABS_TO_DROPDOWN_THRESHOLD;
      viewerBody.innerHTML = `
        ${renderAsTree ? `
          <div class="readonly-content-viewer">
            ${renderContentTree(content.items, selected?.topicId)}
            <div id="purchase-frame-wrap" style="flex:1;min-height:0;width:100%;overflow:hidden;background:#fff;position:relative;">
              <iframe id="purchase-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
            </div>
          </div>
        ` : `
          ${contentItems.length > 1 ? (useDropdown
            ? `<select class="item-select" style="margin-bottom:10px;max-width:100%;flex-shrink:0;">
                ${contentItems.map((it, i) => `<option value="${i}">${escapeHtml(it.title)}</option>`).join('')}
              </select>`
            : `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px;flex-shrink:0;">
                ${contentItems.map((it, i) => `<button class="btn btn-sm item-tab" data-i="${i}">${escapeHtml(it.title)}</button>`).join('')}
              </div>`) : ''}
          <div id="purchase-frame-wrap" style="flex:1;min-height:0;width:100%;overflow:hidden;background:#fff;position:relative;">
            <iframe id="purchase-frame" sandbox="allow-scripts" allowfullscreen allow="fullscreen" style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
          </div>
        `}
        <div style="display:flex;align-items:center;justify-content:space-between;margin-top:10px;flex-wrap:wrap;gap:8px;flex-shrink:0;">
          <p style="color:var(--text-dim);font-size:0.8rem;margin:0;">View access only - the source file is never downloadable through Learning Lab.</p>
          <div id="vote-controls" style="display:flex;gap:8px;"></div>
        </div>
      `;

      function renderVoteControls() {
        const wrap = viewerBody.querySelector('#vote-controls');
        wrap.innerHTML = `
          <button class="btn btn-sm vote-btn" data-value="up" style="${voteState.yourVote === 'up' ? 'border-color:var(--accent);color:var(--accent);' : ''}">Up ${voteState.upvotes}</button>
          <button class="btn btn-sm vote-btn" data-value="down" style="${voteState.yourVote === 'down' ? 'border-color:var(--danger);color:var(--danger);' : ''}">Down ${voteState.downvotes}</button>
        `;
        wrap.querySelectorAll('.vote-btn').forEach((b) => {
          b.addEventListener('click', async () => {
            const nextValue = voteState.yourVote === b.dataset.value ? null : b.dataset.value;
            wrap.querySelectorAll('.vote-btn').forEach((x) => { x.disabled = true; });
            try { voteState = await voteListing(listingId, nextValue); renderVoteControls(); }
            catch (err) { showToast(friendlyError(err, 'Could not record your vote.')); renderVoteControls(); }
          });
        });
      }

      function showItem(item) {
        detachAskAI?.();
        detachQuizScore?.();
        const frame = viewerBody.querySelector('#purchase-frame');
        const wrap = viewerBody.querySelector('#purchase-frame-wrap');
        if (!item || !frame || !wrap) return;
        const spinner = document.createElement('div');
        spinner.className = 'content-loading-overlay';
        spinner.innerHTML = '<div class="content-loading-spinner"></div>';
        wrap.appendChild(spinner);
        frame.addEventListener('load', () => spinner.remove(), { once: true });
        loadReadOnlyFrame(frame, item.html, { courseId: content.courseId, topicId: item.topicId });
        detachAskAI = attachAskAIBridge(frame, { topicTitle: item.title || content.title, courseId: content.courseId, topicId: item.topicId, ownerUid: content.sellerUid });
        detachQuizScore = attachPurchaseQuizScoreBridge(frame, { purchaseId, topicId: item.topicId });
      }

      renderVoteControls();
      if (renderAsTree) wireContentTree(viewerBody.querySelector('.readonly-content-tree'), content.items, { initialTopicId: selected?.topicId, onSelect: showItem });
      else showItem(selected);
      viewerBody.querySelectorAll('.item-tab').forEach((b) => b.addEventListener('click', () => showItem(contentItems[Number(b.dataset.i)])));
      viewerBody.querySelector('.item-select')?.addEventListener('change', (e) => showItem(contentItems[Number(e.target.value)]));
    } catch (err) {
      overlay.querySelector('#viewer-body').innerHTML = `<div class="empty-state">${escapeHtml(friendlyError(err, "Couldn't load this content."))}</div>`;
    }
  }
  loadBody();
}

﻿function openReportPurchaseModal(purchase) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal" style="width:min(92vw,560px);">
      <h2>Report item</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Describe what is inappropriate, incorrect, or misleading about "${escapeHtml(purchase?.listingTitle || 'this item')}". Admin will review it.</p>
      <div class="field">
        <label>Your complaint</label>
        <textarea id="purchase-complaint" rows="6" maxlength="3000" placeholder="Explain the issue clearly..."></textarea>
      </div>
      <div id="report-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions">
        <button class="btn" id="report-cancel">Cancel</button>
        <button class="btn btn-primary" id="report-submit">Submit complaint</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#report-cancel').addEventListener('click', () => overlay.remove());
  overlay.querySelector('#report-submit').addEventListener('click', async () => {
    const btn = overlay.querySelector('#report-submit');
    const errEl = overlay.querySelector('#report-error');
    errEl.textContent = '';
    btn.disabled = true;
    btn.textContent = 'Submitting...';
    try {
      await reportPurchase(purchase.id, overlay.querySelector('#purchase-complaint').value);
      overlay.remove();
      showToast('Complaint submitted for admin review.');
    } catch (err) {
      errEl.textContent = friendlyError(err, 'Could not submit complaint.');
      btn.disabled = false;
      btn.textContent = 'Submit complaint';
    }
  });
}

async function openOrganizeModal(uid, purchase) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal">
      <h2>Organize "${escapeHtml(purchase?.listingTitle || 'Untitled')}"</h2>
      <p style="color:var(--text-dim);margin-top:-8px;">Choose where this shows up in your own course tree. This does not move or copy anything.</p>
      <div class="field">
        <label>Course</label>
        <select id="org-course"><option value="">Not organized</option></select>
      </div>
      <div class="field">
        <label>Topic / subtopic</label>
        <select id="org-topic" disabled><option value="">Top level</option></select>
      </div>
      <div id="org-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
      <div class="modal-actions"><button class="btn" id="cancel-btn">Cancel</button><button class="btn btn-primary" id="save-btn">Save</button></div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('#cancel-btn').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const courseSelect = overlay.querySelector('#org-course');
  const topicSelect = overlay.querySelector('#org-topic');
  const courses = await getCourses(uid).catch(() => []);
  courseSelect.innerHTML += courses.map((c) => `<option value="${c.id}" ${purchase?.filedCourseId === c.id ? 'selected' : ''}>${escapeHtml(c.emoji || '')} ${escapeHtml(c.title || 'Untitled')}</option>`).join('');

  async function loadTopicOptions(courseId) {
    topicSelect.disabled = true;
    topicSelect.innerHTML = '<option value="">Top level</option>';
    if (!courseId) return;
    const topics = await getTopics(uid, courseId).catch(() => []);
    const active = topics.filter((t) => !t.deletedAt);
    const byParent = {};
    active.forEach((t) => { (byParent[t.parentTopicId || ''] ||= []).push(t); });
    const lines = [];
    function walk(parentId, depth) {
      (byParent[parentId] || []).forEach((t) => {
        lines.push(`<option value="${t.id}" ${purchase?.filedTopicId === t.id ? 'selected' : ''}>${'-- '.repeat(depth)}${t.kind === 'index' ? '[Folder] ' : ''}${escapeHtml(t.title || 'Untitled')}</option>`);
        walk(t.id, depth + 1);
      });
    }
    walk('', 0);
    topicSelect.innerHTML += lines.join('');
    topicSelect.disabled = false;
  }

  courseSelect.addEventListener('change', () => loadTopicOptions(courseSelect.value));
  await loadTopicOptions(courseSelect.value);

  overlay.querySelector('#save-btn').addEventListener('click', async () => {
    const btn = overlay.querySelector('#save-btn');
    btn.disabled = true;
    try {
      await filePurchase(purchase.id, courseSelect.value || null, courseSelect.value ? (topicSelect.value || null) : null);
      overlay.remove();
      showToast(courseSelect.value ? 'Organized - check your course tree.' : 'Removed from your tree.');
    } catch (err) {
      overlay.querySelector('#org-error').textContent = friendlyError(err, 'Could not organize this item.');
      btn.disabled = false;
    }
  });
}


// both need an explicit "Approve" click before buyers see the new thing.
const APPROVAL_NOTICE = `Buyers only ever see content you've explicitly approved.
Listing something for sale does NOT make it visible right away — click "Approve current version" once to publish it.
Later, whenever you build a new version of the same course/topic, buyers keep seeing the version you last approved until you approve again — nothing goes out to them automatically.`;

// Grouping key for "is this the same underlying content as another
// listing" — course listings by courseId, topic listings by topicId.
// listForSale now refuses to ever create a second listing per group (see
// functions/marketplace.js), but listings created before that guard existed
// can still collide, and when they do, a buyer of the "other" one never
// sees updates the seller approves on this one. This banner exists to
// surface that specific, otherwise-invisible failure mode.
function duplicateGroupKey(l) {
  return l.type === 'course' ? `course:${l.courseId}` : `topic:${l.topicId}`;
}

function renderSellingTab(body, uid, unsubs) {
  // Listing new content in the Library is admin-only now (see
  // functions/marketplace.js listForSaleImpl) — a non-admin account only
  // ever sees anything here if it already had listings from before that
  // policy, which it can still manage (delist/approve/edit) below.
  const isAdmin = isAdminCached();
  body.innerHTML = `
    <p style="color:var(--text-dim);">${isAdmin
      ? 'List courses/topics for sale from the course page ("Sell this course" / "Sell this topic"). Manage existing listings here.'
      : 'Only Learning Lab administrators can add new content to the Library. Any listings you already had are still managed here.'}</p>
    <div class="pill" style="display:block;white-space:pre-line;line-height:1.5;padding:12px 14px;margin:10px 0 4px;background:var(--surface-2);">⚠️ ${APPROVAL_NOTICE}</div>
    <div id="mp-duplicate-warning"></div>
    <div id="mp-my-listings"></div>
  `;
  const list = body.querySelector('#mp-my-listings');
  const dupWarning = body.querySelector('#mp-duplicate-warning');

  const unsub = watchMyListings(uid, (listings) => {
    const byGroup = {};
    listings.forEach((l) => { (byGroup[duplicateGroupKey(l)] ||= []).push(l); });
    const duplicateGroups = Object.values(byGroup).filter((g) => g.length > 1);

    dupWarning.innerHTML = duplicateGroups.length === 0 ? '' : `
      <div class="pill" style="display:block;line-height:1.5;padding:12px 14px;margin:0 0 14px;background:var(--danger);color:#fff;">
        ⚠️ <strong>${duplicateGroups.length} item${duplicateGroups.length > 1 ? 's' : ''} have more than one listing for the same content.</strong>
        This can happen from listings created before duplicate-checking was added. If a buyer purchased the listing you're
        <em>not</em> updating, they'll never see your changes. Click "Approve current version" on <strong>every</strong> listing
        marked below until you're confident which one (if any) is safe to delist — delisting doesn't remove access for buyers
        who already purchased that specific listing, so approve first, clean up later.
      </div>
    `;
    const duplicateListingIds = new Set(duplicateGroups.flat().map((l) => l.id));

    if (listings.length === 0) {
      list.innerHTML = `<div class="empty-state">
        <div style="font-size:2.5rem;">🏷️</div>
        <h3>You haven't listed anything yet</h3>
        <p>${isAdmin ? 'Open a course, then use "Sell this course" or "Sell this topic".' : 'Only Learning Lab administrators can add new content to the Library.'}</p>
      </div>`;
      return;
    }
    list.innerHTML = `<div class="grid-cards">${listings.map((l) => `
      <div class="card" data-listing-id="${l.id}" style="cursor:default;${duplicateListingIds.has(l.id) ? 'border-color:var(--danger);' : ''}">
        <div style="display:flex;justify-content:space-between;">
          <div class="card-emoji">${l.emoji || '📘'}</div>
          <span class="pill">${l.status === 'active' ? 'Active' : 'Delisted'}${duplicateListingIds.has(l.id) ? ' · ⚠️ duplicate' : ''}</span>
        </div>
        <h3>${escapeHtml(l.title)}</h3>
        <p>${l.type === 'course' ? 'Whole course' : 'Single topic'} · ${ACCESS_DURATION_LABELS[l.accessDuration] || '1 year'}</p>
        <p style="margin:4px 0;">${formatPrice(l.viewPrice)}</p>
        <p>👍 ${l.upvotes || 0} 👎 ${l.downvotes || 0}</p>
        <p class="approval-status pill">Checking approval status…</p>
        <div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:10px;">
          <button class="btn btn-sm approve-btn" data-id="${l.id}">Approve current version</button>
          <button class="btn btn-sm btn-ghost check-btn" data-id="${l.id}">Check content quality</button>
          <button class="btn btn-sm toggle-btn" data-id="${l.id}" data-status="${l.status}">${l.status === 'active' ? 'Delist' : 'Relist'}</button>
          <button class="btn btn-sm btn-ghost remove-listing-btn" data-id="${l.id}" data-title="${escapeHtml(l.title)}" title="Permanently remove this listing (only once no buyer has active access)">Remove</button>
        </div>
      </div>
    `).join('')}</div>`;

    // Approval status needs an extra read (the seller's own topic/course
    // current version, compared against what the listing has approved) so
    // it's filled in asynchronously per card rather than blocking the
    // initial render.
    listings.forEach((l) => {
      computeApprovalStatus(l).then((status) => {
        const card = list.querySelector(`.card[data-listing-id="${l.id}"] .approval-status`);
        if (!card) return;
        const { text, color } = APPROVAL_STATUS_DISPLAY[status];
        card.textContent = text;
        card.style.color = color;
        card.style.background = 'var(--surface-2)';
      }).catch(() => {});
    });

    list.querySelectorAll('.approve-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Approving…';
        try {
          await approveVersion(btn.dataset.id);
          showToast('Approved — buyers will now see this version.');
        } catch (err) {
          showToast(friendlyError(err, 'Could not approve version.'));
        } finally {
          btn.disabled = false;
          btn.textContent = 'Approve current version';
        }
      });
    });
    // Advisory only — see checkListingContent above. Never blocks approval;
    // just reports what the automated checks noticed so the seller can
    // decide for themselves whether it's worth fixing.
    list.querySelectorAll('.check-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Checking…';
        try {
          const listing = listings.find((item) => item.id === btn.dataset.id);
          const problems = await checkListingContent(uid, listing);
          if (problems.length === 0) {
            await alertDialog('No issues found by the automated checks.', { title: 'Content check' });
          } else {
            await alertDialog(problems.join('\n\n'), { title: `${problems.length} possible issue${problems.length > 1 ? 's' : ''} found` });
          }
        } catch (err) {
          showToast(friendlyError(err, 'Could not run the content check.'));
        } finally {
          btn.disabled = false;
          btn.textContent = 'Check content quality';
        }
      });
    });
    list.querySelectorAll('.toggle-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          if (btn.dataset.status === 'active') await delistListing(btn.dataset.id);
          else await relistListing(btn.dataset.id);
        } catch (err) {
          showToast(friendlyError(err, 'Could not update listing.'));
        } finally {
          btn.disabled = false;
        }
      });
    });
    list.querySelectorAll('.remove-listing-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const ok = await confirmDialog(
          `Permanently remove "${btn.dataset.title}" from the marketplace? This can't be undone (unlike Delist). `
          + "Your own course/topic is untouched — only the listing itself goes away.",
          { title: 'Remove listing?', okText: 'Remove', danger: true },
        );
        if (!ok) return;
        btn.disabled = true;
        btn.textContent = 'Removing…';
        try {
          await deleteListingForever(btn.dataset.id);
          showToast('Listing removed.');
        } catch (err) {
          await alertDialog(friendlyError(err, 'Could not remove this listing.'), { title: "Can't remove" });
          btn.disabled = false;
          btn.textContent = 'Remove';
        }
      });
    });
  });
  unsubs.push(unsub);
}

const APPROVAL_STATUS_DISPLAY = {
  unapproved: { text: '🔴 Not approved yet — invisible to buyers', color: 'var(--danger)' },
  'update-available': { text: '🟠 Update available — approve to publish it', color: 'var(--warning, #d97706)' },
  'up-to-date': { text: '🟢 Approved — buyers see the current version', color: 'var(--success)' },
};

// Compares what the seller has actually approved against the seller's own
// current generated version(s), so "My listings" can say plainly whether a
// listing is (a) never approved, so it's invisible to buyers regardless of
// price, (b) approved but stale, because the seller built a newer version
// since the last approval and buyers are still on the old one, or (c) fully
// up to date. This is a plain owner-scoped Firestore read (the seller
// reading their own courses/topics — same rules as everywhere else in the
// app), not a marketplace/Cloud-Function call.
async function computeApprovalStatus(listing) {
  if (listing.type === 'topic') {
    if (!listing.approvedVersion) return 'unapproved';
    const topic = await getTopic(listing.sellerUid, listing.courseId, listing.topicId);
    if (topic?.currentVersion && topic.currentVersion !== listing.approvedVersion) return 'update-available';
    return 'up-to-date';
  }
  const approved = listing.approvedVersions || {};
  if (Object.keys(approved).length === 0) return 'unapproved';
  const topicsSnap = await getDocs(collection(db, 'users', listing.sellerUid, 'courses', listing.courseId, 'topics'));
  let updateAvailable = false;
  topicsSnap.docs.forEach((d) => {
    const t = d.data();
    // A trashed topic (deletedAt set) is never included in approvedVersions
    // in the first place — functions/marketplace.js's approveVersionImpl
    // deliberately skips it via loadCourseTopics' isTrashed filter, the same
    // "not something the creator has chosen to distribute" reasoning as
    // everywhere else this filter appears. Comparing against it here anyway
    // made a trashed-but-previously-generated topic look like a permanent,
    // unfixable "update available" — clicking Approve could never resolve it,
    // because the server would keep skipping that same topic every time.
    if (t.deletedAt) return;
    if (t.currentVersion && approved[d.id] !== t.currentVersion) updateAvailable = true;
  });
  return updateAvailable ? 'update-available' : 'up-to-date';
}
